import { sampleMediaMemberFields, sampleMemberMediaFromRow } from './sampleMedia';
import { getProductReviewFeatures } from '@garagegymbros/data';
import { mapNotifications, NOTIFICATION_COLUMNS } from './notificationRefresh';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  accountRoleSchema,
  profileUpdateSchema,
  socialLinkSchema,
  storySchema,
  uuidSchema,
} from '@garagegymbros/contracts';
import { effectiveStaffRole, readStaffAccessStatus } from './staffAccess';
import { catalogDimensions, loadCatalogMetadata } from './catalogMetadata';
import type {
  AppState,
  Member,
  Product,
  OwnedItem,
  Post,
  Story,
  Topic,
  Listing,
  Conversation,
  Review,
  Report,
} from './types';
type Row = Record<string, any>;
const WINDOW = 500;
const bootstrapColumns: Record<string, string> = {
  profiles:
    'id,display_name,username,bio,location,visibility,status,avatar_media_id,is_demo,sample_media',
  products:
    'id,manufacturer_id,category_id,slug,name,description,specifications,source_url,status,is_demo,width_mm,depth_mm,height_mm',
  manufacturers: 'id,name',
  equipment_categories: 'id,name',
  product_variants: 'id,product_id,name,sku,specifications,width_mm,depth_mm,height_mm',
  product_offers: 'product_id,currency,amount,observed_at,availability',
  catalog_assets: 'product_id,kind,status,validation_status',
  // Older hosted schemas omit optional established_year; retain tolerant property reads.
  gyms: '*',
  owned_items:
    'id,owner_id,gym_id,product_id,variant_id,custom_name,quantity,condition,ownership_status,visibility,notes,position',
  owned_item_purchase_records:
    'id,item_id,owner_id,amount,currency,source,purchased_on,private_notes,created_at',
  social_links: 'id,user_id,platform,url,handle,visibility,position',
  // Optional photo metadata must remain compatible with pre-Flex-photo hosted schemas.
  stories: '*',
  story_views: 'story_id,user_id',
  story_responses: 'story_id,user_id,kind',
  follows: 'follower_id,following_id,status',
  blocks: 'user_id,blocked_user_id',
  mutes: 'user_id,muted_user_id',
  forum_categories: 'id,name,description,position',
  notifications: NOTIFICATION_COLUMNS,
  reports: 'id,user_id,target_type,target_id,reason,status,created_at',
};
function check<T = Row[]>(result: {
  data: unknown;
  error: { message: string; code?: string } | null;
}): T {
  if (result.error)
    throw new Error(
      result.error.code === 'P0429'
        ? 'Wait a moment before trying this action again.'
        : result.error.message,
    );
  return result.data as T;
}
function same(a: unknown, b: unknown) {
  return JSON.stringify(a) === JSON.stringify(b);
}
function index<T extends { id: string }>(items: T[]) {
  return new Map(items.map((item) => [item.id, item]));
}
function initials(name: string) {
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word[0] ?? '')
      .join('')
      .toUpperCase() || 'G'
  );
}
function condition(value: string) {
  const normalized = value.toLowerCase().trim().replace(/\s+/g, '_');
  return normalized === 'used' ? 'fair' : normalized;
}
function specValues(value: Row | undefined): Record<string, string | number | null> {
  return Object.fromEntries(
    Object.entries(value ?? {}).map(([key, v]) => [
      key,
      typeof v === 'number' || typeof v === 'string' || v === null
        ? v
        : typeof v === 'object' && v !== null && 'value' in v
          ? `${v.value} ${v.unit ?? ''}`.trim()
          : String(v),
    ]),
  );
}
function typeForCategory(category: string) {
  const value = category.toLowerCase();
  if (/rack|smith|trainer|machine/.test(value)) return 'rack';
  if (/bench/.test(value)) return 'bench';
  if (/barbell|bar/.test(value)) return 'barbell';
  if (/plate/.test(value)) return 'plates';
  if (/cardio|treadmill|bike/.test(value)) return 'cardio';
  return 'dumbbells';
}
/** Focused bootstrap snapshot. Every query uses the caller JWT and RLS; community surfaces page separately. */
export async function loadLiveState(
  client: SupabaseClient,
  userId: string,
  signal?: AbortSignal,
): Promise<AppState> {
  signal?.throwIfAborted();
  async function rows(
    table: string,
    filter?: [string, string],
    order?: string,
    limit = WINDOW,
  ): Promise<Row[]> {
    let query = client
      .from(table)
      .select(bootstrapColumns[table] ?? '*')
      .limit(limit);
    if (filter) query = query.eq(...filter);
    if (table === 'follows') query = query.or(`follower_id.eq.${userId},following_id.eq.${userId}`);
    if (table === 'profiles') query = query.eq('visibility', 'public');
    if (table === 'products') query = query.in('status', ['published', 'retired']);
    if (table === 'stories')
      query = query.eq('status', 'published').gt('expires_at', new Date().toISOString());
    if (order) query = query.order(order, { ascending: false });
    return check<Row[]>(await (signal ? query.abortSignal(signal) : query));
  }
  const tableNames = [
    'profiles',
    'social_links',
    'gyms',
    'manufacturers',
    'equipment_categories',
    'products',
    'product_variants',
    'product_offers',
    'catalog_assets',
    'owned_items',
    'owned_item_purchase_records',
    'product_reviews',
    'review_helpful_votes',
    'posts',
    'post_comments',
    'post_reactions',
    'post_equipment_tags',
    'stories',
    'story_views',
    'story_responses',
    'follows',
    'blocks',
    'mutes',
    'forum_categories',
    'forum_topics',
    'forum_replies',
    'forum_subscriptions',
    'trade_listings',
    'conversations',
    'conversation_participants',
    'messages',
    'notifications',
    'reports',
    'audit_events',
  ] as const;
  // Feed, catalog reviews, conversations, forum and trade hydrate through their own pagers.
  // Initial boot transfers owned/private records, small discovery metadata and active stories.
  const deferred = new Set<string>([
    'product_variants',
    'product_offers',
    'catalog_assets',
    'product_reviews',
    'review_helpful_votes',
    'posts',
    'post_comments',
    'post_reactions',
    'post_equipment_tags',
    'story_views',
    'story_responses',
    'forum_topics',
    'forum_replies',
    'forum_subscriptions',
    'trade_listings',
    'conversations',
    'conversation_participants',
    'messages',
    'audit_events',
  ]);
  const ownFilters: Record<string, [string, string]> = {
    social_links: ['user_id', userId],
    gyms: ['owner_id', userId],
    owned_items: ['owner_id', userId],
    owned_item_purchase_records: ['owner_id', userId],
    blocks: ['user_id', userId],
    mutes: ['user_id', userId],
    notifications: ['user_id', userId],
    reports: ['user_id', userId],
  };
  const limits: Record<string, number> = {
    profiles: 12,
    products: 24,
    stories: 20,
    manufacturers: 200,
    equipment_categories: 200,
    forum_categories: 100,
    social_links: 100,
    gyms: 20,
    blocks: 1000,
    mutes: 1000,
    follows: 1000,
    reports: 50,
  };
  const profileQuery = client.from('profiles').select(bootstrapColumns.profiles).eq('id', userId);
  const settingsQuery = client
    .from('private_account_settings')
    .select('catalog_view,notification_preferences')
    .eq('user_id', userId);
  const accessQuery = client.rpc('staff_access_status'),
    roleQuery = client.rpc('account_role');
  const [sets, ownProfileResult, settingsResult, accessResult, accountRoleResult, ownStories] =
    await Promise.all([
      Promise.all(
        tableNames.map((table) =>
          deferred.has(table)
            ? Promise.resolve([])
            : rows(
                table,
                ownFilters[table],
                [
                  'profiles',
                  'products',
                  'stories',
                  'owned_item_purchase_records',
                  'notifications',
                  'reports',
                  'follows',
                ].includes(table)
                  ? 'created_at'
                  : undefined,
                limits[table] ?? WINDOW,
              ),
        ),
      ),
      (signal ? profileQuery.abortSignal(signal) : profileQuery).maybeSingle(),
      (signal ? settingsQuery.abortSignal(signal) : settingsQuery).maybeSingle(),
      signal ? accessQuery.abortSignal(signal) : accessQuery,
      signal ? roleQuery.abortSignal(signal) : roleQuery,
      rows('stories', ['user_id', userId], 'created_at', 24),
    ]);
  signal?.throwIfAborted();
  const data = Object.fromEntries(tableNames.map((name, i) => [name, sets[i]!])) as Record<
    (typeof tableNames)[number],
    Row[]
  >;
  const ownProfile = check<Row | null>(ownProfileResult);
  data.stories = [
    ...new Map([...data.stories, ...ownStories].map((story) => [story.id, story])).values(),
  ];
  async function byIds(table: string, column: string, ids: string[], limit = 1000): Promise<Row[]> {
    const unique = [...new Set(ids.filter(Boolean))],
      chunks: string[][] = [];
    for (let offset = 0; offset < unique.length; offset += 100)
      chunks.push(unique.slice(offset, offset + 100));
    const parts = await Promise.all(
      chunks.map((values) => {
        const query = client
          .from(table)
          .select(bootstrapColumns[table] ?? '*')
          .in(column, values)
          .limit(limit);
        return signal ? query.abortSignal(signal) : query;
      }),
    );
    return parts.flatMap((result) => check<Row[]>(result));
  }
  const knownMembers = new Set([
    ...data.profiles.map((profile) => profile.id),
    ...(ownProfile ? [ownProfile.id] : []),
  ]);
  const knownProducts = new Set(data.products.map((product) => product.id));
  const relatedIds = [
    ...data.stories.map((story) => story.user_id),
    ...data.notifications.map((notice) => notice.actor_id),
    ...data.follows.flatMap((follow) => [follow.follower_id, follow.following_id]),
  ].filter((id) => id && !knownMembers.has(id));
  const [relatedProfiles, ownedProducts, views, responses, catalogMetadata] = await Promise.all([
    byIds('profiles', 'id', relatedIds),
    byIds(
      'products',
      'id',
      data.owned_items.map((item) => item.product_id).filter((id) => !knownProducts.has(id)),
    ),
    byIds(
      'story_views',
      'story_id',
      data.stories.map((story) => story.id),
    ),
    byIds(
      'story_responses',
      'story_id',
      data.stories.map((story) => story.id),
    ),
    loadCatalogMetadata(
      client,
      data.owned_items.map((item) => item.product_id),
      signal,
    ),
  ]);
  signal?.throwIfAborted();
  data.profiles = [
    ...new Map(
      [...data.profiles, ...relatedProfiles, ...(ownProfile ? [ownProfile] : [])].map((profile) => [
        profile.id,
        profile,
      ]),
    ).values(),
  ];
  data.products = [
    ...new Map(
      [...data.products, ...ownedProducts].map((product) => [product.id, product]),
    ).values(),
  ];
  data.story_views = views;
  data.story_responses = responses;
  const productIds = data.products.map((product) => product.id);
  [data.product_variants, data.product_offers, data.catalog_assets] = await Promise.all([
    byIds('product_variants', 'product_id', productIds),
    byIds('product_offers', 'product_id', productIds),
    byIds('catalog_assets', 'product_id', productIds),
  ]);
  const settings = check<Row | null>(settingsResult),
    staffAccess = readStaffAccessStatus(accessResult.error ? null : accessResult.data),
    accountRole = accountRoleSchema.safeParse(
      accountRoleResult.error ? null : accountRoleResult.data,
    );
  const productRows = index(data.products as Array<Row & { id: string }>),
    categoryRows = index(data.equipment_categories as Array<Row & { id: string }>),
    manufacturerRows = index(data.manufacturers as Array<Row & { id: string }>);
  const members: Member[] = data.profiles.map((p) => ({
    id: p.id,
    display_name: p.display_name,
    username: p.username,
    bio: p.bio ?? '',
    location: p.location ?? '',
    avatar_media_id: p.avatar_media_id ?? undefined,
    ...sampleMediaMemberFields(p),
    avatar_color: ['#606060', '#505050', '#707070', '#5a5a5a'][
      Array.from(p.id as string).reduce((sum, c) => sum + c.charCodeAt(0), 0) % 4
    ]!,
    initials: initials(p.display_name),
    is_private: p.visibility !== 'public',
    profile_visibility: p.visibility,
    gym_name:
      data.gyms.find((g) => g.owner_id === p.id && g.status === 'active')?.name ??
      sampleMemberMediaFromRow(p)?.gym_name ??
      'My garage gym',
    established_year: (() => {
      const year = data.gyms.find(
        (g) => g.owner_id === p.id && g.status === 'active',
      )?.established_year;
      return typeof year === 'number' ? year : undefined;
    })(),
    status: p.status,
    social_links: data.social_links
      .filter((l) => l.user_id === p.id)
      .map((l) => ({
        id: l.id,
        platform: l.platform,
        url: l.url,
        handle: l.handle ?? undefined,
        visibility: l.visibility,
        position: l.position,
      })),
  }));
  const products: Product[] = data.products.map((p) => {
    const category = categoryRows.get(p.category_id)?.name ?? 'Equipment';
    const offer = data.product_offers
      .filter(
        (o) =>
          o.product_id === p.id &&
          o.currency === 'USD' &&
          o.amount !== null &&
          Date.parse(o.observed_at) <= Date.now() &&
          Date.parse(o.observed_at) > Date.now() - 7 * 86400000 &&
          ['in_stock', 'unknown'].includes(o.availability),
      )
      .sort((a, b) => Date.parse(b.observed_at) - Date.parse(a.observed_at))[0];
    return {
      id: p.id,
      manufacturer_id: p.manufacturer_id,
      brand: manufacturerRows.get(p.manufacturer_id)?.name ?? 'Manufacturer',
      name: p.name,
      category,
      slug: p.slug,
      description: p.description,
      status: p.status,
      is_demo: p.is_demo,
      variants: data.product_variants
        .filter((v) => v.product_id === p.id)
        .map((v) => ({
          id: v.id,
          product_id: v.product_id,
          name: v.name,
          sku: v.sku ?? undefined,
          specifications: specValues(v.specifications),
          dimensions: catalogDimensions(v),
        })),
      specifications: specValues(p.specifications),
      source_url: p.source_url ?? undefined,
      observed_at: offer?.observed_at,
      price: offer?.amount === undefined ? undefined : Number(offer.amount),
      currency: offer?.currency,
      image_kind: typeForCategory(category),
      ...catalogMetadata.get(p.id),
      dimensions: catalogMetadata.get(p.id)?.dimensions ?? catalogDimensions(p),
      has_3d: data.catalog_assets.some(
        (a) =>
          a.product_id === p.id &&
          a.kind === 'interactive_3d' &&
          a.status === 'approved' &&
          a.validation_status === 'validated',
      ),
    };
  });
  const owned_items: OwnedItem[] = data.owned_items.map((i) => {
    const purchase = data.owned_item_purchase_records
      .filter((p) => p.item_id === i.id && p.owner_id === userId)
      .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))[0];
    return {
      id: i.id,
      owner_id: i.owner_id,
      gym_id: i.gym_id,
      product_id: i.product_id ?? undefined,
      variant_id: i.variant_id ?? undefined,
      custom_name: i.custom_name ?? productRows.get(i.product_id)?.name ?? 'Equipment',
      quantity: i.quantity,
      condition: i.condition,
      ownership_status: i.ownership_status,
      visibility: i.visibility,
      notes: i.notes,
      position: i.position,
      ...(purchase
        ? {
            purchase: {
              id: purchase.id,
              amount: purchase.amount === null ? undefined : Number(purchase.amount),
              currency: purchase.currency ?? 'USD',
              source: purchase.source ?? '',
              purchased_on: purchase.purchased_on ?? '',
              private_notes: purchase.private_notes ?? '',
            },
          }
        : {}),
    };
  });
  const reviews: Review[] = data.product_reviews.map((r) => ({
    id: r.id,
    product_id: r.product_id,
    author_id: r.user_id,
    variant_id: r.variant_id ?? undefined,
    rating: r.rating,
    title: r.title,
    body: r.body,
    status: r.status,
    created_at: r.created_at,
    ownership_declaration: r.ownership_declaration,
    disclosure: r.affiliation,
    helpful_ids: data.review_helpful_votes
      .filter((v) => v.review_id === r.id)
      .map((v) => v.user_id),
  }));
  const posts: Post[] = data.posts.map((p) => ({
    id: p.id,
    author_id: p.user_id,
    body: p.body,
    created_at: p.created_at,
    visibility: p.visibility,
    equipment_item_id: p.equipment_item_id ?? undefined,
    review_id: p.review_id ?? undefined,
    review: p.review_id
      ? reviews
          .filter((review) => review.id === p.review_id)
          .map((review) => ({
            ...review,
            product: products.find((product) => product.id === review.product_id),
          }))[0]
      : undefined,
    images: Array.isArray(p.photo_media_ids)
      ? p.photo_media_ids.map((id: string) => `https://media.garagegymbros.invalid/${id}`)
      : undefined,
    image:
      p.image_url ??
      members.find((member) => member.id === p.user_id)?.sample_media?.post_images[p.id],
    reactions: data.post_reactions.filter((r) => r.post_id === p.id).map((r) => r.user_id),
    comments: data.post_comments
      .filter((c) => c.post_id === p.id)
      .map((c) => ({
        id: c.id,
        author_id: c.user_id,
        body: c.body,
        created_at: c.created_at,
        parent_id: c.parent_id ?? null,
      }))
      .sort((a, b) => a.created_at.localeCompare(b.created_at)),
    equipment_tags: data.post_equipment_tags
      .filter((t) => t.post_id === p.id)
      .map((t) => t.product_id ?? t.owned_item_id),
  }));
  const stories: Story[] = data.stories
    .filter((s) => s.status === 'published' && Date.parse(s.expires_at) > Date.now())
    .map((s) => ({
      id: s.id,
      author_id: s.user_id,
      body: s.body,
      created_at: s.created_at,
      expires_at: s.expires_at,
      ...(s.duration_hours === 24 || s.duration_hours === 48
        ? { duration_hours: s.duration_hours }
        : {}),
      visibility: s.visibility ?? 'public',
      image:
        s.image_url ??
        members.find((member) => member.id === s.user_id)?.sample_media?.story_images[s.id],
      views: data.story_views.filter((v) => v.story_id === s.id).map((v) => v.user_id),
      reactions: data.story_responses
        .filter((v) => v.story_id === s.id && v.kind === 'dumbbell')
        .map((v) => v.user_id),
    }));
  const topics: Topic[] = data.forum_topics.map((t) => ({
    id: t.id,
    category_id: t.category_id,
    author_id: t.user_id,
    title: t.title,
    body: t.body,
    created_at: t.created_at,
    tags: t.tags ?? [],
    accepted_reply_id: t.accepted_reply_id ?? undefined,
    replies: data.forum_replies
      .filter((r) => r.topic_id === t.id)
      .map((r) => ({ id: r.id, author_id: r.user_id, body: r.body, created_at: r.created_at }))
      .sort((a, b) => a.created_at.localeCompare(b.created_at)),
    subscriber_ids: data.forum_subscriptions
      .filter((s) => s.topic_id === t.id)
      .map((s) => s.user_id),
  }));
  const listings: Listing[] = data.trade_listings
    .filter((l) => ['available', 'reserved', 'completed', 'withdrawn'].includes(l.status))
    .map((l) => {
      const item = data.owned_items.find((i) => i.id === l.owned_item_id),
        product = productRows.get(item?.product_id);
      return {
        id: l.id,
        author_id: l.user_id,
        owned_item_id: l.owned_item_id,
        title: l.title,
        description: l.description,
        category: l.category ?? categoryRows.get(product?.category_id)?.name ?? 'Equipment',
        condition: l.condition ?? item?.condition ?? 'Condition not recorded',
        location: l.approximate_location,
        status: l.status,
        trade_for: l.desired_trade,
        created_at: l.created_at,
        expires_at: l.expires_at,
        asking_value: l.asking_value === null ? undefined : Number(l.asking_value),
        currency: l.currency ?? 'USD',
      };
    });
  const conversations: Conversation[] = data.conversations
    .filter(
      (c) =>
        c.status !== 'declined' &&
        !data.conversation_participants.some(
          (p) => p.conversation_id === c.id && p.user_id === userId && p.hidden_at,
        ),
    )
    .map((c) => {
      const participant = data.conversation_participants.find(
          (p) => p.conversation_id === c.id && p.user_id === userId,
        ),
        messages = data.messages
          .filter((m) => m.conversation_id === c.id)
          .map((m) => ({
            id: m.id,
            author_id: m.user_id ?? 'deleted-member',
            body: m.body,
            created_at: m.created_at,
          }))
          .sort((a, b) => a.created_at.localeCompare(b.created_at));
      return {
        id: c.id,
        member_ids: [c.requester_id ?? 'deleted-member', c.recipient_id ?? 'deleted-member'],
        requester_id: c.requester_id ?? 'deleted-member',
        moderation_notice: c.moderation_notice === true,
        status: c.status,
        messages,
        last_read_at: participant?.last_read_at ?? null,
        unread_count: messages.filter(
          (m) =>
            m.author_id !== userId &&
            (!participant?.last_read_at || m.created_at > participant.last_read_at),
        ).length,
      };
    });
  return {
    version: 1,
    current_user_id: userId,
    members,
    products,
    owned_items,
    reviews,
    posts,
    stories,
    follows: data.follows.map((f) => ({
      follower_id: f.follower_id,
      following_id: f.following_id,
      status: f.status,
    })),
    blocks: data.blocks.filter((b) => b.user_id === userId).map((b) => b.blocked_user_id),
    mutes: data.mutes.filter((m) => m.user_id === userId).map((m) => m.muted_user_id),
    forum_categories: data.forum_categories
      .sort((a, b) => a.position - b.position)
      .map((c) => ({ id: c.id, name: c.name, description: c.description })),
    topics,
    listings,
    conversations,
    notifications: mapNotifications(data.notifications, userId, members, data.messages),
    notification_preferences: {
      ...(settings?.notification_preferences ?? {}),
      activity_email: false,
    },
    reports: data.reports.map((r) => ({
      id: r.id,
      reporter_id: r.user_id,
      target_type: r.target_type,
      target_id: r.target_id,
      reason: r.reason,
      status: r.status,
      created_at: r.created_at,
    })),
    audit_events: data.audit_events.map((a) => ({
      id: a.id,
      action: a.action,
      created_at: a.created_at,
      details: a.reason,
    })),
    catalog_view: settings?.catalog_view ?? 'grid',
    demo_role: effectiveStaffRole(staffAccess),
    staff_access: staffAccess,
    account_role: accountRole.success ? accountRole.data : (staffAccess.role ?? 'member'),
    deactivated: data.profiles.find((p) => p.id === userId)?.status === 'deactivated',
  };
}

/** Diff only permitted user actions; server UUIDs replace temporary UI UUIDs in next. */
export async function persistChanges(
  client: SupabaseClient,
  prev: AppState,
  next: AppState,
  userId: string,
): Promise<void> {
  if (next.current_user_id !== userId || prev.current_user_id !== userId)
    throw new Error('Account switching is available only in the fictional demo.');
  if (
    next.demo_role !== prev.demo_role ||
    next.account_role !== prev.account_role ||
    !same(next.staff_access, prev.staff_access)
  )
    throw new Error(
      'Account roles can only change through the protected owner or administrator operation.',
    );
  if (
    !same(prev.products, next.products) ||
    !same(prev.forum_categories, next.forum_categories) ||
    !same(prev.audit_events, next.audit_events)
  )
    throw new Error('Use the protected administration operation for these changes.');
  const own = (id: string) => {
    if (id !== userId) throw new Error('You can edit only your own records.');
  };
  const rpc = async (name: string, args?: Row) => check<any>(await client.rpc(name, args));
  const write = async (
    table: string,
    item: { id: string },
    old: { id: string } | undefined,
    payload: Row,
  ): Promise<Row> => {
    const result = old
      ? await client.from(table).update(payload).eq('id', old.id).select().single()
      : await client.from(table).insert(payload).select().single();
    const row = check<Row>(result);
    if (!row) throw new Error('This record is no longer available.');
    item.id = row.id;
    return row;
  };
  const remove = async (table: string, id: string) => {
    check(await client.from(table).delete().eq('id', id));
  };
  const delta = <T extends { id: string }>(before: T[], after: T[]) => ({
    old: index(before),
    removed: before.filter((item) => !after.some((v) => v.id === item.id)),
    changed: after.filter(
      (item) =>
        !same(
          before.find((v) => v.id === item.id),
          item,
        ),
    ),
  });
  async function toggle(
    table: string,
    targetField: string,
    targetId: string,
    before: string[],
    after: string[],
  ) {
    const added = after.filter((id) => !before.includes(id)),
      removed = before.filter((id) => !after.includes(id));
    for (const id of [...added, ...removed]) own(id);
    if (added.length)
      check(
        await client
          .from(table)
          .upsert(
            { [targetField]: targetId, user_id: userId },
            { onConflict: `${targetField},user_id`, ignoreDuplicates: true },
          ),
      );
    if (removed.length)
      check(await client.from(table).delete().eq(targetField, targetId).eq('user_id', userId));
  }
  const mine = next.members.find((m) => m.id === userId),
    oldMine = prev.members.find((m) => m.id === userId);
  for (const member of next.members) {
    if (
      member.id !== userId &&
      !same(
        member,
        prev.members.find((m) => m.id === member.id),
      )
    )
      throw new Error('Another member’s profile cannot be changed.');
  }
  if (mine && oldMine) {
    if (mine.status !== oldMine.status)
      throw new Error('Account status requires a protected operation.');
    const profile = (m: Member) => ({
      username: m.username,
      display_name: m.display_name,
      bio: m.bio,
      location: m.location || null,
      visibility: m.is_private
        ? (m as Member & { profile_visibility?: string }).profile_visibility === 'private'
          ? 'private'
          : 'followers'
        : 'public',
    });
    if (!same(profile(mine), profile(oldMine))) {
      const result = await client
        .from('profiles')
        .update(profileUpdateSchema.parse(profile(mine)))
        .eq('id', userId);
      if (result.error?.code === '23505')
        throw new Error('That profile name is already taken. Choose another.');
      if (
        result.error?.code === '23514' &&
        result.error.message.includes('profiles_username_check')
      )
        throw new Error('Choose a valid profile name that is not reserved for a site page.');
      check(result);
    }
    const social = delta(oldMine.social_links ?? [], mine.social_links ?? []);
    for (const link of social.removed) await remove('social_links', link.id);
    for (const link of social.changed)
      await write('social_links', link, social.old.get(link.id), {
        ...socialLinkSchema.parse({
          platform: link.platform,
          url: link.url,
          handle: link.handle ?? null,
          visibility: link.visibility,
          position: link.position,
        }),
        ...(!social.old.has(link.id) ? { user_id: userId } : {}),
      });
    if (mine.gym_name !== oldMine.gym_name || mine.established_year !== oldMine.established_year) {
      const gym = check<Row | null>(
        await client
          .from('gyms')
          .select('id')
          .eq('owner_id', userId)
          .eq('status', 'active')
          .maybeSingle(),
      );
      const establishedYear =
        typeof mine.established_year === 'number' ? mine.established_year : null;
      if (gym)
        check(
          await client
            .from('gyms')
            .update({ name: mine.gym_name, established_year: establishedYear })
            .eq('id', gym.id),
        );
      else
        check(
          await client.from('gyms').insert({
            owner_id: userId,
            name: mine.gym_name,
            established_year: establishedYear,
            visibility: mine.is_private ? 'followers' : 'public',
          }),
        );
    }
  }
  next.notification_preferences = { ...next.notification_preferences, activity_email: false };
  if (
    !same(prev.notification_preferences, next.notification_preferences) ||
    prev.catalog_view !== next.catalog_view
  )
    check(
      await client
        .from('private_account_settings')
        .update({
          notification_preferences: { ...next.notification_preferences, activity_email: false },
          catalog_view: next.catalog_view,
        })
        .eq('user_id', userId),
    );
  if (prev.deactivated !== next.deactivated)
    await rpc(next.deactivated ? 'deactivate_account' : 'reactivate_account');
  for (const [table, column, before, after] of [
    ['blocks', 'blocked_user_id', prev.blocks, next.blocks],
    ['mutes', 'muted_user_id', prev.mutes, next.mutes],
  ] as const) {
    for (const target of after.filter((id) => !before.includes(id)))
      check(
        await client
          .from(table)
          .upsert(
            { user_id: userId, [column]: target },
            { onConflict: `user_id,${column}`, ignoreDuplicates: true },
          ),
      );
    for (const target of before.filter((id) => !after.includes(id)))
      check(await client.from(table).delete().eq('user_id', userId).eq(column, target));
  }
  const followKey = (f: AppState['follows'][number]) => f.follower_id + ':' + f.following_id;
  for (const follow of prev.follows.filter(
    (f) => !next.follows.some((n) => followKey(n) === followKey(f)),
  )) {
    if (![follow.follower_id, follow.following_id].includes(userId))
      throw new Error('This relationship is not yours.');
    check(
      await client
        .from('follows')
        .delete()
        .eq('follower_id', follow.follower_id)
        .eq('following_id', follow.following_id),
    );
  }
  for (const follow of next.follows) {
    const old = prev.follows.find((f) => followKey(f) === followKey(follow));
    if (!old) {
      own(follow.follower_id);
      const row = check<Row>(
        await client
          .from('follows')
          .insert({ follower_id: userId, following_id: follow.following_id })
          .select()
          .single(),
      );
      follow.status = row.status;
    } else if (old.status !== follow.status) {
      own(follow.following_id);
      if (follow.status !== 'accepted')
        throw new Error('Only pending follow requests can be accepted.');
      check(
        await client
          .from('follows')
          .update({ status: 'accepted' })
          .eq('follower_id', follow.follower_id)
          .eq('following_id', userId),
      );
    }
  }
  const items = delta(prev.owned_items, next.owned_items);
  const acquiredItemIds: string[] = [];
  for (const item of items.removed) {
    own(item.owner_id);
    await remove('owned_items', item.id);
  }
  let activeGym: string | undefined;
  for (const item of items.changed) {
    own(item.owner_id);
    const old = items.old.get(item.id),
      oldId = item.id;
    if (!item.gym_id) {
      if (!activeGym) {
        const existing = check<Row | null>(
          await client
            .from('gyms')
            .select('id')
            .eq('owner_id', userId)
            .eq('status', 'active')
            .maybeSingle(),
        );
        activeGym = existing?.id;
        if (!activeGym) {
          const gym = check<Row>(
            await client
              .from('gyms')
              .insert({
                owner_id: userId,
                name: mine?.gym_name || 'My garage gym',
                visibility: mine?.is_private ? 'followers' : 'public',
              })
              .select()
              .single(),
          );
          activeGym = gym.id;
        }
      }
      item.gym_id = activeGym!;
    }
    const payload = {
      gym_id: item.gym_id,
      product_id: item.product_id || null,
      variant_id: item.variant_id || null,
      custom_name: item.custom_name || null,
      quantity: item.quantity,
      condition: condition(item.condition),
      ownership_status: item.ownership_status,
      visibility: item.visibility,
      notes: item.notes,
      position: item.position,
    };
    const oldPayload = old
      ? {
          gym_id: old.gym_id,
          product_id: old.product_id || null,
          variant_id: old.variant_id || null,
          custom_name: old.custom_name || null,
          quantity: old.quantity,
          condition: condition(old.condition),
          ownership_status: old.ownership_status,
          visibility: old.visibility,
          notes: old.notes,
          position: old.position,
        }
      : null;
    if (!same(payload, oldPayload))
      await write('owned_items', item, old, { ...payload, ...(!old ? { owner_id: userId } : {}) });
    if (item.ownership_status === 'owned' && old?.ownership_status !== 'owned')
      acquiredItemIds.push(item.id);
    if (item.id !== oldId) {
      for (const listing of next.listings)
        if (listing.owned_item_id === oldId) listing.owned_item_id = item.id;
      for (const post of next.posts)
        if (post.equipment_tags)
          post.equipment_tags = post.equipment_tags.map((id) => (id === oldId ? item.id : id));
    }
    if (item.purchase && !same(item.purchase, old?.purchase)) {
      const purchase = item.purchase;
      const p = {
        amount: purchase.amount ?? null,
        currency: purchase.currency || 'USD',
        source: purchase.source || null,
        purchased_on: purchase.purchased_on || null,
        private_notes: purchase.private_notes ?? '',
      };
      if (purchase.id)
        check(
          await client
            .from('owned_item_purchase_records')
            .update(p)
            .eq('id', purchase.id)
            .eq('owner_id', userId),
        );
      else {
        const inserted = check<Row>(
          await client
            .from('owned_item_purchase_records')
            .insert({ ...p, item_id: item.id, owner_id: userId })
            .select()
            .single(),
        );
        purchase.id = inserted.id;
      }
    }
  }
  const reviews = delta(prev.reviews, next.reviews);
  for (const review of reviews.removed) {
    own(review.author_id);
    await remove('product_reviews', review.id);
  }
  for (const review of reviews.changed) {
    const old = reviews.old.get(review.id);
    if (
      old &&
      old.status !== review.status &&
      same(
        [
          old.rating,
          old.title,
          old.body,
          old.variant_id,
          old.disclosure,
          old.ownership_declaration,
        ],
        [
          review.rating,
          review.title,
          review.body,
          review.variant_id,
          review.disclosure,
          review.ownership_declaration,
        ],
      )
    )
      throw new Error('Review publication status is controlled by moderation.');
    const content = (r: Review) => ({
      variant_id: r.variant_id || null,
      rating: r.rating,
      title: r.title,
      body: r.body,
      ownership_declaration:
        r.ownership_declaration === 'listed_in_gym' ? 'owned' : r.ownership_declaration,
      affiliation: r.disclosure || 'none',
    });
    if (!old || !same(content(review), content(old))) {
      own(review.author_id);
      if (!Number.isInteger(review.rating) && !(await getProductReviewFeatures(client)).halfStars)
        throw new Error(
          'Half-star review saving is awaiting backend activation. Whole-star reviews remain available.',
        );
      const row = await write('product_reviews', review, old, {
        ...content(review),
        ...(!old ? { user_id: userId, product_id: review.product_id } : {}),
      });
      review.status = row.status;
    }
    await toggle(
      'review_helpful_votes',
      'review_id',
      review.id,
      old?.helpful_ids ?? [],
      review.helpful_ids,
    );
  }
  const posts = delta(prev.posts, next.posts);
  for (const post of posts.removed) {
    own(post.author_id);
    await remove('posts', post.id);
  }
  for (const post of posts.changed) {
    const old = posts.old.get(post.id);
    if (
      !old ||
      post.body !== old.body ||
      post.visibility !== old.visibility ||
      post.image !== old.image
    ) {
      own(post.author_id);
      await write('posts', post, old, {
        body: post.body,
        visibility: post.visibility,
        image_url: post.image ?? null,
        ...(!old ? { user_id: userId } : {}),
      });
    }
    await toggle('post_reactions', 'post_id', post.id, old?.reactions ?? [], post.reactions);
    const comments = delta(old?.comments ?? [], post.comments);
    for (const c of comments.removed) {
      own(c.author_id);
      await remove('post_comments', c.id);
    }
    for (const c of comments.changed) {
      own(c.author_id);
      await write('post_comments', c, comments.old.get(c.id), {
        body: c.body,
        ...(!comments.old.has(c.id)
          ? { post_id: post.id, user_id: userId, parent_id: c.parent_id ?? null }
          : {}),
      });
    }
    for (const tag of post.equipment_tags ?? []) {
      if (!(old?.equipment_tags ?? []).includes(tag)) {
        own(post.author_id);
        check(
          await client.from('post_equipment_tags').insert({
            post_id: post.id,
            ...(next.products.some((p) => p.id === tag)
              ? { product_id: tag }
              : { owned_item_id: tag }),
          }),
        );
      }
    }
    for (const tag of old?.equipment_tags ?? []) {
      if (!(post.equipment_tags ?? []).includes(tag)) {
        own(post.author_id);
        check(
          await client
            .from('post_equipment_tags')
            .delete()
            .eq('post_id', post.id)
            .eq(next.products.some((p) => p.id === tag) ? 'product_id' : 'owned_item_id', tag),
        );
      }
    }
  }
  const stories = delta(prev.stories, next.stories);
  for (const story of stories.removed) {
    own(story.author_id);
    await remove('stories', story.id);
  }
  for (const story of stories.changed) {
    const old = stories.old.get(story.id);
    if (!old) {
      own(story.author_id);
      const input = storySchema.parse({
        body: story.body,
        visibility: story.visibility ?? 'public',
        ...(story.image ? { image_url: story.image } : {}),
        ...(story.duration_hours ? { duration_hours: story.duration_hours } : {}),
      });
      const payload = {
        user_id: userId,
        body: input.body,
        visibility: input.visibility,
        ...(input.duration_hours ? { duration_hours: input.duration_hours } : {}),
      };
      let row: Row;
      if (input.image_url || input.duration_hours !== undefined) {
        // Installed photo/duration contracts permit a stable client UUID. A lost
        // response cannot publish duplicate Flexes or consume the rate limit twice.
        const id = uuidSchema.parse(story.id);
        const result = await client
          .from('stories')
          .insert({ ...payload, id, ...(input.image_url ? { image_url: input.image_url } : {}) })
          .select()
          .single();
        if (result.error?.code === '23505') {
          const existing = check<Row | null>(
            await client
              .from('stories')
              .select('*')
              .eq('id', id)
              .eq('user_id', userId)
              .maybeSingle(),
          );
          if (
            !existing ||
            existing.user_id !== userId ||
            existing.body !== input.body ||
            (existing.image_url ?? null) !== (input.image_url ?? null) ||
            existing.visibility !== input.visibility ||
            (input.duration_hours !== undefined && existing.duration_hours !== input.duration_hours)
          )
            throw new Error('This Flex could not be retried safely. Refresh before trying again.');
          row = existing;
        } else row = check<Row>(result);
        if (!row) throw new Error('Your Flex could not be loaded after publication.');
        story.id = row.id;
      } else {
        // Legacy text/video writes omit the new column and client-id grant.
        row = await write('stories', story, undefined, payload);
      }
      story.created_at = row.created_at;
      story.expires_at = row.expires_at;
      if (row.duration_hours === 24 || row.duration_hours === 48)
        story.duration_hours = row.duration_hours;
    } else if (
      story.body !== old.body ||
      story.image !== old.image ||
      story.visibility !== old.visibility
    )
      throw new Error('Delete and replace a Flex to change it.');
    else if (story.duration_hours !== old.duration_hours || story.expires_at !== old.expires_at)
      throw new Error('Use Keep for 48 hours to extend an active Flex.');
    await toggle('story_views', 'story_id', story.id, old?.views ?? [], story.views);
    const before = old?.reactions ?? [],
      after = story.reactions;
    for (const id of [
      ...after.filter((id) => !before.includes(id)),
      ...before.filter((id) => !after.includes(id)),
    ])
      own(id);
    if (after.includes(userId) && !before.includes(userId)) {
      const result = await client
        .from('story_responses')
        .insert({ story_id: story.id, user_id: userId, kind: 'dumbbell' });
      if (result.error?.code !== '23505') check(result);
    }
    if (before.includes(userId) && !after.includes(userId))
      check(
        await client
          .from('story_responses')
          .delete()
          .eq('story_id', story.id)
          .eq('user_id', userId)
          .eq('kind', 'dumbbell'),
      );
  }
  const topics = delta(prev.topics, next.topics);
  for (const topic of topics.removed) {
    own(topic.author_id);
    await remove('forum_topics', topic.id);
  }
  for (const topic of topics.changed) {
    const old = topics.old.get(topic.id);
    if (
      !old ||
      !same(
        [topic.title, topic.body, topic.category_id, topic.tags],
        [old.title, old.body, old.category_id, old.tags],
      )
    ) {
      own(topic.author_id);
      await write('forum_topics', topic, old, {
        title: topic.title,
        body: topic.body,
        category_id: topic.category_id,
        tags: topic.tags ?? [],
        ...(!old ? { user_id: userId, kind: 'question' } : {}),
      });
    }
    const replies = delta(old?.replies ?? [], topic.replies);
    for (const reply of replies.removed) {
      own(reply.author_id);
      if (old?.accepted_reply_id === reply.id)
        await rpc('accept_forum_answer', { topic_id: topic.id, reply_id: null });
      await remove('forum_replies', reply.id);
    }
    for (const reply of replies.changed) {
      own(reply.author_id);
      const tempId = reply.id;
      await write('forum_replies', reply, replies.old.get(reply.id), {
        body: reply.body,
        ...(!replies.old.has(reply.id) ? { user_id: userId, topic_id: topic.id } : {}),
      });
      if (topic.accepted_reply_id === tempId) topic.accepted_reply_id = reply.id;
    }
    if (topic.accepted_reply_id !== old?.accepted_reply_id)
      await rpc('accept_forum_answer', {
        topic_id: topic.id,
        reply_id: topic.accepted_reply_id ?? null,
      });
    await toggle(
      'forum_subscriptions',
      'topic_id',
      topic.id,
      old?.subscriber_ids ?? [],
      topic.subscriber_ids,
    );
  }
  const listings = delta(prev.listings, next.listings);
  for (const listing of listings.removed) {
    own(listing.author_id);
    await remove('trade_listings', listing.id);
  }
  for (const listing of listings.changed) {
    own(listing.author_id);
    const old = listings.old.get(listing.id),
      payload = (l: Listing) => ({
        title: l.title,
        description: l.description,
        category: l.category,
        condition: condition(l.condition),
        desired_trade: l.trade_for,
        approximate_location: l.location,
        asking_value: l.asking_value ?? null,
        currency: l.currency ?? 'USD',
      });
    if (!old || !same(payload(listing), payload(old))) {
      if (!listing.owned_item_id)
        throw new Error('Choose equipment from My Gym before listing a trade.');
      await write('trade_listings', listing, old, {
        ...payload(listing),
        ...(!old
          ? {
              user_id: userId,
              owned_item_id: listing.owned_item_id,
              expires_at: listing.expires_at ?? new Date(Date.now() + 30 * 86400000).toISOString(),
            }
          : {}),
      });
    }
    if (old && old.status !== listing.status)
      await rpc('transition_trade', { listing_id: listing.id, new_status: listing.status });
  }
  const conversations = delta(prev.conversations, next.conversations);
  for (const conversation of conversations.removed) {
    if (conversation.status === 'pending' && conversation.requester_id !== userId)
      await rpc('respond_conversation', { conversation_id: conversation.id, accept: false });
    else
      check(
        await client
          .from('conversation_participants')
          .update({ hidden_at: new Date().toISOString() })
          .eq('conversation_id', conversation.id)
          .eq('user_id', userId),
      );
  }
  for (const conversation of conversations.changed) {
    const old = conversations.old.get(conversation.id);
    if (!conversation.member_ids.includes(userId))
      throw new Error('You are not a participant in this conversation.');
    if (!old) {
      own(conversation.requester_id);
      const other = conversation.member_ids.find((id) => id !== userId);
      if (!other || conversation.member_ids.length !== 2)
        throw new Error('Choose one member to message.');
      conversation.id = await rpc('request_conversation', { other_user: other });
      const actual = check<Row>(
        await client.from('conversations').select('*').eq('id', conversation.id).single(),
      );
      if (actual.status === 'declined')
        throw new Error('The previous message request was declined. A new request cannot be sent.');
      conversation.status = actual.status;
      conversation.requester_id = actual.requester_id ?? 'deleted-member';
      conversation.member_ids = [
        actual.requester_id ?? 'deleted-member',
        actual.recipient_id ?? 'deleted-member',
      ];
      check(
        await client
          .from('conversation_participants')
          .update({ hidden_at: null })
          .eq('conversation_id', conversation.id)
          .eq('user_id', userId),
      );
      if (
        actual.status === 'pending' &&
        actual.requester_id !== userId &&
        conversation.messages.length
      )
        throw new Error('Accept the existing incoming message request before replying.');
    } else if (old.status !== conversation.status)
      await rpc('respond_conversation', { conversation_id: conversation.id, accept: true });
    if (
      old &&
      old.messages.some(
        (message) => !conversation.messages.some((m) => m.id === message.id && same(m, message)),
      )
    )
      throw new Error(
        'Sent messages cannot be edited or erased from another participant’s history. Hide the conversation instead.',
      );
    for (const message of conversation.messages.filter(
      (m) => !old?.messages.some((p) => p.id === m.id),
    )) {
      own(message.author_id);
      const result = await rpc('send_message', {
        conversation_id: conversation.id,
        body: message.body,
        client_nonce: message.id,
      });
      message.id = result.id;
      message.created_at = result.created_at;
    }
    if (old && conversation.unread_count !== old.unread_count && conversation.unread_count === 0)
      check(
        await client
          .from('conversation_participants')
          .update({ last_read_at: new Date().toISOString() })
          .eq('conversation_id', conversation.id)
          .eq('user_id', userId),
      );
  }
  for (const notification of next.notifications) {
    const old = prev.notifications.find((n) => n.id === notification.id);
    if (!old) throw new Error('Notifications are created by server events.');
    if (old.read !== notification.read)
      check(
        await client
          .from('notifications')
          .update({ read_at: notification.read ? new Date().toISOString() : null })
          .eq('id', notification.id)
          .eq('user_id', userId),
      );
  }
  for (const report of next.reports) {
    const old = prev.reports.find((r) => r.id === report.id);
    if (old) {
      if (!same(report, old)) throw new Error('Use protected moderation to change report status.');
      continue;
    }
    own(report.reporter_id);
    let targetType = report.target_type === 'trade_listing' ? 'trade' : report.target_type,
      targetId = report.target_id;
    if (targetType === 'conversation') {
      const conversation = next.conversations.find((c) => c.id === targetId);
      const message = conversation?.messages.at(-1);
      if (!message) throw new Error('Report a specific message, or report this member’s profile.');
      targetType = 'message';
      targetId = message.id;
    }
    await write('reports', report, undefined, {
      user_id: userId,
      target_type: targetType,
      target_id: targetId,
      reason: report.reason,
    });
  }
  // The item transaction owns publication. Hydrate its canonical rows only after
  // normal post persistence, so these are never inserted a second time by clients.
  if (acquiredItemIds.length) {
    const activities = check<Row[]>(
      await client
        .from('posts')
        .select('id,user_id,body,visibility,created_at,equipment_item_id')
        .eq('user_id', userId)
        .eq('status', 'published')
        .in('equipment_item_id', acquiredItemIds),
    );
    const existingPostIds = new Set(next.posts.map((post) => post.id));
    for (const activity of activities) {
      if (existingPostIds.has(activity.id)) continue;
      next.posts.unshift({
        id: activity.id,
        author_id: activity.user_id,
        body: activity.body,
        visibility: activity.visibility,
        created_at: activity.created_at,
        equipment_item_id: activity.equipment_item_id,
        equipment_tags: [activity.equipment_item_id],
        reactions: [],
        comments: [],
      });
      existingPostIds.add(activity.id);
    }
  }
}
